import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import type { QuestionContent, LongAnswerContent } from '@/types';
import styles from './QuestionEditor.module.scss';

interface LongAnswerEditorProps {
  content: QuestionContent | null;
  onChange: (content: QuestionContent) => void;
}

export function LongAnswerEditor({ content, onChange }: LongAnswerEditorProps) {
  const [minLength, setMinLength] = useState<number>(100);
  const [maxLength, setMaxLength] = useState<number>(2000);
  const [rubric, setRubric] = useState<string[]>(['']);
  const [sampleAnswer, setSampleAnswer] = useState('');
  const [isAutoGraded, setIsAutoGraded] = useState(false);

  // Initialize from existing content
  useEffect(() => {
    if (content && content.type === 'long_answer') {
      setMinLength(content.minLength || 100);
      setMaxLength(content.maxLength || 2000);
      setRubric(content.rubric || ['']);
      setSampleAnswer(content.sampleAnswer || '');
      setIsAutoGraded(content.isAutoGraded);
    }
  }, [content]);

  // Update parent when data changes
  useEffect(() => {
    const newContent: LongAnswerContent = {
      type: 'long_answer',
      minLength,
      maxLength,
      rubric: rubric.filter((item) => item.trim() !== ''),
      sampleAnswer: sampleAnswer.trim() !== '' ? sampleAnswer : undefined,
      isAutoGraded,
    };
    onChange(newContent);
  }, [minLength, maxLength, rubric, sampleAnswer, isAutoGraded, onChange]);

  const handleRubricChange = (value: string) => {
    setRubric(value.split('\n').filter((line) => line.trim() !== ''));
  };

  return (
    <div className={styles.editor}>
      <h4 className={styles.editor__heading}>Long Answer / Essay Configuration</h4>

      <div className={styles.editor__info}>
        Long answer questions allow for detailed, multi-paragraph responses.
        {!isAutoGraded && ' These will require manual grading.'}
      </div>

      <div className={styles.editor__field}>
        <label htmlFor="min-length" className={styles.editor__label}>
          Minimum Length (characters)
        </label>
        <input
          id="min-length"
          type="number"
          className={styles.editor__input}
          value={minLength}
          onChange={(e) => setMinLength(parseInt(e.target.value, 10))}
          min={50}
          max={5000}
        />
      </div>

      <div className={styles.editor__field}>
        <label htmlFor="max-length" className={styles.editor__label}>
          Maximum Length (characters)
        </label>
        <input
          id="max-length"
          type="number"
          className={styles.editor__input}
          value={maxLength}
          onChange={(e) => setMaxLength(parseInt(e.target.value, 10))}
          min={100}
          max={10000}
        />
      </div>

      <div className={styles.editor__field}>
        <label htmlFor="rubric" className={styles.editor__label}>
          Grading Rubric (one criterion per line)
        </label>
        <textarea
          id="rubric"
          className={styles.editor__textarea}
          value={rubric.join('\n')}
          onChange={(e) => handleRubricChange(e.target.value)}
          placeholder="e.g.,&#10;Clearly states thesis&#10;Provides supporting evidence&#10;Uses proper citations&#10;Well-organized structure"
          rows={6}
        />
        <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
          Define the criteria for evaluating student responses
        </p>
      </div>

      <div className={styles.editor__field}>
        <label htmlFor="sample-answer" className={styles.editor__label}>
          Sample Answer (optional)
        </label>
        <textarea
          id="sample-answer"
          className={styles.editor__textarea}
          value={sampleAnswer}
          onChange={(e) => setSampleAnswer(e.target.value)}
          placeholder="Provide an example of a high-quality answer..."
          rows={8}
          maxLength={5000}
        />
        <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
          This helps students understand what a complete answer looks like
        </p>
      </div>

      <div className={styles.editor__field}>
        <label className={styles.editor__checkboxLabel}>
          <input
            type="checkbox"
            className={styles.editor__checkbox}
            checked={isAutoGraded}
            onChange={(e) => setIsAutoGraded(e.target.checked)}
          />
          <span className={styles.editor__checkboxText}>
            Enable auto-grading (experimental)
          </span>
        </label>
        {isAutoGraded && (
          <motion.p
            className={styles.editor__info}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            style={{ marginTop: '8px' }}
          >
            Auto-grading will use AI to evaluate responses against the rubric and sample answer.
            Manual review is still recommended for important assessments.
          </motion.p>
        )}
      </div>
    </div>
  );
}
